import * as fabricTypes from 'fabric'
import { fabric } from '~/utils/editor/fabric'
import { assignNewColorAndAlpha, makeFabricGradient } from '~/utils/ColorHelper'
import { DefaultEditorHeight, DefaultEditorWidth } from '~/utils/editor/object.class'
import { FillType } from '~/static/enums/components/color-input'
import { ActualVersion as BackgroundProps } from './versioning/background'

export class Background {
  width: number = DefaultEditorWidth
  height: number = DefaultEditorHeight
  borderRadius: number = 0
  hasBackground: boolean = false
  backgroundType: FillType = FillType.Solid
  backgroundColor: FabricGradientOptions | string = 'rgba(255,255,255,1)'
  backgroundOpacity: number = 100
  border: number = 1
  borderColor: string = 'rgba(0,0,0,1)'
  borderOpacity: number = 100
  borderDash: boolean = false
  borderExists: boolean = false

  object: fabricTypes.Rect

  constructor(props?: BackgroundProps) {
    this.object = new fabric.Rect({
      selectable: false,
      evented: false,
    })
    this.set(props ?? this)
  }

  /**
   * Set the properties of the background.
   * If the property is not defined in the props object, it will not be changed.
   * If the property is defined in the props object, it will be updated with the new value.
   * @param {BackgroundProps} props - The properties of the background to be set.
   */
  set(props: BackgroundProps) {
    this.width = props.width ?? this.width
    this.height = props.height ?? this.height
    this.borderRadius = props.borderRadius ?? this.borderRadius
    this.backgroundType = props.backgroundType ?? this.backgroundType
    this.border = props.border ?? this.border
    this.borderDash = props.borderDash ?? this.borderDash
    this.borderExists = props.borderExists ?? this.borderExists
    this.hasBackground = props.hasBackground ?? this.hasBackground

    const color = assignNewColorAndAlpha(
      this.backgroundType,
      this.backgroundColor,
      this.backgroundOpacity,
      props.backgroundColor,
      props.backgroundOpacity,
    )
    this.backgroundColor = color.color
    this.backgroundOpacity = color.opacity

    const borderColor = assignNewColorAndAlpha(
      FillType.Solid,
      this.borderColor,
      this.borderOpacity,
      props.borderColor,
      props.borderOpacity,
    )
    this.borderColor = borderColor.color as string
    this.borderOpacity = borderColor.opacity

    const borderWidth = this.borderExists ? this.border : 0
    this.object.set({
      width: this.width - borderWidth,
      height: this.height - borderWidth,
      rx: this.borderRadius,
      ry: this.borderRadius,
      fill: this.hasBackground ?
        (typeof this.backgroundColor === 'string') ?
          this.backgroundColor :
          makeFabricGradient(this.width - borderWidth, this.height - borderWidth, this.backgroundColor) :
        undefined,
      strokeWidth: borderWidth,
      stroke: this.borderColor,
      strokeDashArray: this.borderDash ? [ 10 ] : undefined,
    })
  }

  toObject(): object {
    const data: { [ key: string ]: unknown } = {}
    Object.keys(this).forEach((prop) => {
      const thisKey = <keyof typeof this>prop
      if (this[thisKey] !== undefined && prop !== 'data' && prop !== 'object') {
        data[prop] = this[thisKey]
      }
    })
    return data
  }

  toJson(): string {
    return JSON.stringify(this.toObject())
  }
}
